import { SaveService } from '@core/services/SaveService';
import type { PlayerProfileData } from '@core/types';

const DEFAULT_PROFILE: PlayerProfileData = {
  name: '',
  coins: 0,
  highScores: {},
  unlocked: [],
  character: '',
  ownedItems: [],
  accessory: '',
  ownedBackgrounds: ['stage'],
  background: 'stage',
  gamesPlayed: 0,
  totalCoinsEarned: 0,
  claimedMissions: [],
  dailyDate: '',
  dailyGamesPlayed: 0,
  claimedDailies: [],
};

/**
 * Fuente unica de verdad del estado del jugador. Cualquier escena o modulo lee y
 * escribe el progreso a traves de esta clase, que se encarga de persistirlo.
 * Se inyecta a las escenas mediante el registry global de Phaser.
 */
export class PlayerProfile {
  private data: PlayerProfileData;

  public constructor(private readonly saveService: SaveService) {
    const loaded = saveService.load();
    // Mezclamos con los valores por defecto para que perfiles guardados con versiones
    // anteriores reciban los campos nuevos (ej. character) sin romperse.
    this.data = loaded
      ? { ...structuredClone(DEFAULT_PROFILE), ...loaded }
      : structuredClone(DEFAULT_PROFILE);
  }

  public get name(): string {
    return this.data.name;
  }

  public get coins(): number {
    return this.data.coins;
  }

  public setName(name: string): void {
    this.data.name = name.trim();
    this.persist();
  }

  public get character(): string {
    return this.data.character;
  }

  public setCharacter(characterKey: string): void {
    this.data.character = characterKey;
    this.persist();
  }

  public addCoins(amount: number): void {
    if (amount <= 0) {
      return;
    }
    this.data.coins += amount;
    this.data.totalCoinsEarned += amount;
    this.persist();
  }

  public get gamesPlayed(): number {
    return this.data.gamesPlayed;
  }

  public get totalCoinsEarned(): number {
    return this.data.totalCoinsEarned;
  }

  /** Registra que se jugo una partida (para misiones y estadisticas). */
  public recordPlay(): void {
    this.ensureDaily();
    this.data.gamesPlayed += 1;
    this.data.dailyGamesPlayed += 1;
    this.persist();
  }

  public isMissionClaimed(missionId: string): boolean {
    return this.data.claimedMissions.includes(missionId);
  }

  /** Marca una mision como reclamada y entrega la recompensa en estrellas. */
  public claimMission(missionId: string, reward: number): boolean {
    if (this.isMissionClaimed(missionId)) {
      return false;
    }
    this.data.claimedMissions.push(missionId);
    this.persist();
    this.addCoins(reward);
    return true;
  }

  public get dailyGamesPlayed(): number {
    this.ensureDaily();
    return this.data.dailyGamesPlayed;
  }

  public isDailyClaimed(missionId: string): boolean {
    this.ensureDaily();
    return this.data.claimedDailies.includes(missionId);
  }

  /** Reclama una mision diaria (se reinicia cada dia). */
  public claimDaily(missionId: string, reward: number): boolean {
    this.ensureDaily();
    if (this.data.claimedDailies.includes(missionId)) {
      return false;
    }
    this.data.claimedDailies.push(missionId);
    this.persist();
    this.addCoins(reward);
    return true;
  }

  /** Si cambio el dia, reinicia el progreso y las recompensas diarias. */
  private ensureDaily(): void {
    const today = new Date().toISOString().slice(0, 10);
    if (this.data.dailyDate !== today) {
      this.data.dailyDate = today;
      this.data.dailyGamesPlayed = 0;
      this.data.claimedDailies = [];
      this.persist();
    }
  }

  public getHighScore(minigameKey: string): number {
    return this.data.highScores[minigameKey] ?? 0;
  }

  /** Registra un puntaje y devuelve true si supero el record anterior. */
  public submitScore(minigameKey: string, score: number): boolean {
    const previous = this.getHighScore(minigameKey);
    if (score <= previous) {
      return false;
    }
    this.data.highScores[minigameKey] = score;
    this.persist();
    return true;
  }

  public get ownedItems(): readonly string[] {
    return this.data.ownedItems;
  }

  public ownsItem(itemId: string): boolean {
    return this.data.ownedItems.includes(itemId);
  }

  /** Intenta comprar un item: descuenta monedas si alcanza y no lo tenia. */
  public buyItem(itemId: string, price: number): boolean {
    if (this.ownsItem(itemId) || this.data.coins < price) {
      return false;
    }
    this.data.coins -= price;
    this.data.ownedItems.push(itemId);
    this.persist();
    return true;
  }

  public get accessory(): string {
    return this.data.accessory;
  }

  /** Equipa un accesorio (o lo quita si se pasa cadena vacia). */
  public setAccessory(itemId: string): void {
    this.data.accessory = itemId;
    this.persist();
  }

  public get background(): string {
    return this.data.background;
  }

  public get ownedBackgrounds(): readonly string[] {
    return this.data.ownedBackgrounds;
  }

  public ownsBackground(backgroundId: string): boolean {
    return this.data.ownedBackgrounds.includes(backgroundId);
  }

  /** Intenta comprar un escenario: descuenta estrellas si alcanza y no lo tenia. */
  public buyBackground(backgroundId: string, price: number): boolean {
    if (this.ownsBackground(backgroundId) || this.data.coins < price) {
      return false;
    }
    this.data.coins -= price;
    this.data.ownedBackgrounds.push(backgroundId);
    this.persist();
    return true;
  }

  /** Equipa un escenario como fondo de las pantallas. */
  public setBackground(backgroundId: string): void {
    this.data.background = backgroundId;
    this.persist();
  }

  public isUnlocked(minigameKey: string): boolean {
    return this.data.unlocked.includes(minigameKey);
  }

  public unlock(minigameKey: string): void {
    if (this.isUnlocked(minigameKey)) {
      return;
    }
    this.data.unlocked.push(minigameKey);
    this.persist();
  }

  private persist(): void {
    this.saveService.save(this.data);
  }
}
